import { useAccountStore } from "@/model/account";
import { useLocalStorage } from "@vueuse/core";
import { endpoints, types } from "magnetar-common";
import { defineStore } from "pinia";
import { muteLocationBase, MuteRuleset } from "@/model/weak-mute";
import * as jsonPatch from "fast-json-patch";
import { magToast, ToastAdapterString } from "@/composables/mag-toast";
import { useFluent } from "fluent-vue";
import { shallowRef, toRaw } from "vue";
import { countIf } from "@/types/array";
import { v7 } from "uuid";
import { magApi } from "@/api-client";
import { MuteAdvancedEntries } from "@/model/advanced-mute";

const menuOptions = [
    "notifications",
    "followRequests",
    "explore",
    "favorites",
    "search",
];

export const storeDefaults = {
    version: 1,
    tutorial: 0,
    tlHomeHintClosed: false,
    tlLocalHintClosed: false,
    tlRecommendedHintClosed: false,
    tlSocialHintClosed: false,
    tlGlobalHintClosed: false,
    keepCw: true,
    showFullAcct: false,
    rememberNoteVisibility: false,
    defaultNoteVisibility: "Public" as types.NoteVisibility,
    defaultNoteLocalOnly: false,
    uploadFolder: null as string | null,
    keepOriginalUploading: false,
    memo: null as string | null,
    reactions: [
        "⭐",
        "❤️",
        "😆",
        "🤔",
        "😮",
        "🎉",
        "💢",
        "😥",
        "😇",
        "🦋",
        "🦊",
    ],
    mutedWords: [] as (string[] | string)[],
    weakMutes: [] as MuteRuleset[],
    advancedMutes: { rules: [] } as MuteAdvancedEntries,
    menu: menuOptions,
    visibility: "Public" as types.NoteVisibility,
    localOnly: false,
    statusbars: [] as {
        name: string;
        id: string;
        type: string;
        size: "verySmall" | "small" | "medium" | "large" | "veryLarge";
        black: boolean;
        props: Record<string, any>;
    }[],
    widgets: [
        {
            name: "calendar",
            id: "a",
            place: "right",
            data: {},
        },
        {
            name: "notifications",
            id: "b",
            place: "right",
            data: {},
        },
        {
            name: "trends",
            id: "c",
            place: "right",
            data: {},
        },
    ] as {
        name: string;
        id: string;
        place: string | null;
        data: Record<string, any>;
    }[],
    tl: {
        src: "home" as "home" | "local" | "social" | "global",
        arg: null,
    },
    deviceKindOverride: null as null | "smartphone" | "tablet" | "desktop",
    separateRenoteQuote: true,
    expandOnNoteClick: true,
    nsfw: "respect" as "respect" | "force" | "ignore",
    animation: true,
    advancedMfm: true,
    animatedMfm: true,
    animatedMfmWarnShown: false,
    loadRawImages: false,
    imageNewTab: false,
    disableShowingAnimatedImages: false,
    useOsNativeEmojis: false,
    disableDrawer: false,
    useBlurEffectForModal: true,
    useBlurEffect: true,
    showFixedPostForm: false,
    enableInfiniteScroll: true,
    useReactionPickerForContextMenu: false,
    showGapBetweenNotesInTimeline: true,
    darkMode: false,
    instanceTicker: "remote" as "none" | "remote" | "always",
    reactionPickerSkinTone: 1,
    reactionPickerSize: 3,
    reactionPickerWidth: 3,
    reactionPickerHeight: 3,
    reactionPickerUseDrawerForMobile: true,
    recentlyUsedEmojis: [] as string[],
    recentlyUsedUsers: [] as string[],
    defaultSideView: false,
    menuDisplay: "sideFull" as "sideFull" | "sideIcon" | "top",
    reportError: false,
    squareAvatars: true,
    postFormWithHashtags: false,
    postFormHashtags: "",
    themeInitial: true,
    showUpdates: true,
    enableCustomKaTeXMacro: false,
    enableEmojiReactions: true,
    hideNumbers: false,
    useCompactNumbers: true,
    removeEmojiFromUsernames: false,
    showEmojisInReactionNotifications: true,
    showTimelineReplies: true,
    underlineLinks: false,
};

const valueStorage = {
    version: "remote",
    tutorial: "remote",
    tlHomeHintClosed: "local",
    tlLocalHintClosed: "local",
    tlRecommendedHintClosed: "local",
    tlSocialHintClosed: "local",
    tlGlobalHintClosed: "local",
    keepCw: "remote",
    showFullAcct: "remote",
    rememberNoteVisibility: "remote",
    defaultNoteVisibility: "remote",
    defaultNoteLocalOnly: "remote",
    uploadFolder: "remote",
    keepOriginalUploading: "remote",
    memo: "remote",
    reactions: "remote",
    mutedWords: "remote",
    weakMutes: "remote",
    advancedMutes: "remote",
    menu: "local-per-account",
    visibility: "local-per-account",
    localOnly: "local-per-account",
    statusbars: "local-per-account",
    widgets: "local-per-account",
    tl: "local-per-account",
    deviceKindOverride: "local",
    separateRenoteQuote: "local",
    expandOnNoteClick: "local",
    nsfw: "local",
    animation: "local",
    advancedMfm: "local",
    animatedMfm: "local",
    animatedMfmWarnShown: "local",
    loadRawImages: "local",
    imageNewTab: "local",
    disableShowingAnimatedImages: "local",
    useOsNativeEmojis: "local",
    disableDrawer: "local",
    useBlurEffectForModal: "local",
    useBlurEffect: "local",
    showFixedPostForm: "local",
    enableInfiniteScroll: "local",
    useReactionPickerForContextMenu: "local",
    showGapBetweenNotesInTimeline: "local",
    darkMode: "local",
    instanceTicker: "local",
    reactionPickerSkinTone: "remote",
    reactionPickerSize: "local",
    reactionPickerWidth: "local",
    reactionPickerHeight: "local",
    reactionPickerUseDrawerForMobile: "local",
    recentlyUsedEmojis: "local",
    recentlyUsedUsers: "local",
    defaultSideView: "local",
    menuDisplay: "local",
    reportError: "local",
    squareAvatars: "local",
    postFormWithHashtags: "local",
    postFormHashtags: "local",
    themeInitial: "local",
    showUpdates: "local",
    enableCustomKaTeXMacro: "local",
    enableEmojiReactions: "remote",
    hideNumbers: "remote",
    useCompactNumbers: "remote",
    showEmojisInReactionNotifications: "remote",
    removeEmojiFromUsernames: "remote",
    showTimelineReplies: "local",
    underlineLinks: "remote",
} satisfies {
    [N in keyof typeof storeDefaults]: "local" | "local-per-account" | "remote";
};

export const exportableStoreKeys: (keyof typeof storeDefaults)[] = [
    "menu",
    "visibility",
    "localOnly",
    "statusbars",
    "widgets",
    "tl",
    "deviceKindOverride",
    "nsfw",
    "animation",
    "animatedMfm",
    "loadRawImages",
    "imageNewTab",
    "disableShowingAnimatedImages",
    "useOsNativeEmojis",
    "disableDrawer",
    "useBlurEffectForModal",
    "useBlurEffect",
    "showFixedPostForm",
    "enableInfiniteScroll",
    "useReactionPickerForContextMenu",
    "showGapBetweenNotesInTimeline",
    "instanceTicker",
    "reactionPickerSize",
    "reactionPickerWidth",
    "reactionPickerHeight",
    "reactionPickerUseDrawerForMobile",
    "defaultSideView",
    "menuDisplay",
    "reportError",
    "squareAvatars",
    "showUpdates",
    "enableCustomKaTeXMacro",
    "enableEmojiReactions",
    "showEmojisInReactionNotifications",
    "showTimelineReplies",
];

export const useSettingsStore = defineStore("settings", () => {
    const accountStore = useAccountStore();
    const templateKey = (
        key: string,
        usage: "local" | "local-per-account" | "remote",
    ): string => {
        const userId = accountStore.currentUserId;

        if (usage === "local" || !userId) {
            return key.replace(":user", "@local");
        }

        return key.replace(":user", userId);
    };

    // TODO: Split up the store?

    const settings = useLocalStorage(
        () => templateKey("mag/settings/:user", "local-per-account"),
        storeDefaults,
        {
            deep: true,
            mergeDefaults: true,
        },
    );

    const $reset = () => {
        settings.value = structuredClone(storeDefaults);
    };

    const initialConfig = shallowRef({} as Partial<typeof storeDefaults>);

    const { $t } = useFluent();

    function migrateSettings(
        settings: Partial<typeof storeDefaults>,
    ): Partial<typeof storeDefaults> {
        settings.version ??= 0;

        if (settings.mutedWords && settings.version < 1) {
            const weakMutes = settings.weakMutes ?? [];
            weakMutes.push({
                id: v7(),
                name: "_migrated",
                rules: [
                    {
                        id: v7(),
                        kind: "match-string",
                        strings: settings.mutedWords.flat(),
                        ...muteLocationBase.editor.defaultValue(),
                    },
                ],
            });

            settings.weakMutes = weakMutes;
        }

        settings.version = storeDefaults.version;

        return settings;
    }

    async function fetchConfig(abort: AbortController) {
        if (!accountStore.current) {
            return;
        }

        const config = await magApi(
            endpoints.GetApplicationPreferences,
            {},
            { application: "@magnetar/client/base" },
        ).catch((e) => {
            if (e?.["code"] === "mag::not_found") {
                return {};
            }

            throw e;
        });

        if (abort.signal.aborted) {
            return;
        }

        const oldConfig = toRaw(settings.value);
        const updatedConfig = pickSynchronized(config ?? {});

        initialConfig.value = structuredClone({
            ...updatedConfig,
        });

        updatedConfig.version ??= 0;
        const newConfig = { ...oldConfig, ...updatedConfig };
        const diff = jsonPatch.compare(oldConfig, newConfig, false);

        if (!diff.length && updatedConfig.version >= storeDefaults.version) {
            return;
        }

        settings.value = migrateSettings(newConfig);

        if (!diff.length) {
            return;
        }

        magToast(
            {
                value: $t("status-synced-down-settings", {
                    count: diff.length,
                }),
            },
            ToastAdapterString,
        );
    }

    function pickSynchronized(
        data: Partial<typeof storeDefaults>,
    ): Partial<typeof storeDefaults> {
        return Object.fromEntries(
            Object.entries(data).filter(
                ([k, _]) => valueStorage[k] === "remote",
            ),
        );
    }

    async function syncUpstream(token: string) {
        const oldConfig = initialConfig.value;
        const currentConfig = toRaw(settings.value);
        initialConfig.value = structuredClone(currentConfig);
        const diff = jsonPatch.compare(
            pickSynchronized(oldConfig),
            pickSynchronized(currentConfig),
            true,
        );

        if (!diff.length) {
            return;
        }

        await magApi(
            endpoints.PatchApplicationPreferences,
            diff,
            {
                application: "@magnetar/client/base",
            },
            token,
        )
            .then(() => {
                magToast(
                    {
                        value: $t("status-synced-up-settings", {
                            count: countIf((d) => d.op !== "test", diff),
                        }),
                    },
                    ToastAdapterString,
                );
            })
            .catch((e) => {
                magToast(
                    {
                        value: $t("status-sync-up-settings-failed", {
                            code: e?.["code"] ?? e,
                        }),
                    },
                    ToastAdapterString,
                );
            });
    }

    return { settings, fetchConfig, syncUpstream, $reset };
});
